import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/common/Button';
import { useEventTimeline } from './useEventTimeline';
import {
  normaliseEvent,
  type EventTimelineKind,
  type AnyEventItem,
  type NormalisedEvent,
} from './types';

export interface EventTimelineProps {
  kind: EventTimelineKind;
  parentId: string | number;
}

const PAGE_SIZE = 20;

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const oneHour = 60 * 60 * 1000;

  if (diffMs >= 0 && diffMs < oneHour) {
    const minutes = Math.max(1, Math.floor(diffMs / 60000));
    return `il y a ${String(minutes)} minute${minutes > 1 ? 's' : ''}`;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function EventSkeleton() {
  return (
    <div
      className="animate-pulse rounded-[10px] border border-[#eee] bg-white p-4"
      role="status"
      aria-label="Loading event"
    >
      <div className="mb-2 h-3 w-32 rounded bg-[#f0f0f0]" />
      <div className="mb-2 h-4 w-48 rounded bg-[#f0f0f0]" />
      <div className="h-3 w-full rounded bg-[#f0f0f0]" />
    </div>
  );
}

export function EventTimeline({ kind, parentId }: EventTimelineProps) {
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<NormalisedEvent[]>([]);

  useEffect(() => {
    setOffset(0);
    setAccumulated([]);
  }, [kind, parentId]);

  const { data, isLoading, error } = useEventTimeline({
    kind,
    parentId,
    limit: PAGE_SIZE,
    offset,
  });

  const newItems = useMemo<NormalisedEvent[]>(() => {
    if (!data?.items) return [];
    return data.items.map((raw: AnyEventItem) => normaliseEvent(kind, raw));
  }, [data, kind]);

  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => {
      const seen = new Set(prev.map((e) => e.id));
      const additions = newItems.filter((e) => !seen.has(e.id));
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
  }, [data, newItems]);

  const events = accumulated;
  const total = data?.total ?? events.length;
  const hasMore = events.length < total;

  if (isLoading && events.length === 0) {
    return (
      <div data-testid="event-timeline-loading" className="flex flex-col gap-3">
        <EventSkeleton />
        <EventSkeleton />
        <EventSkeleton />
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div
        data-testid="event-timeline-error"
        className="rounded-[10px] border border-[#ffebee] bg-[#fff5f5] p-4 text-sm text-[#c62828]"
        role="alert"
      >
        Failed to load events: {error.message || 'Unknown error'}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div
        data-testid="event-timeline-empty"
        className="rounded-[10px] border border-dashed border-[#ddd] bg-[#fafafa] p-6 text-center text-sm text-[#666]"
      >
        No events
      </div>
    );
  }

  return (
    <div data-testid="event-timeline" className="flex flex-col gap-3">
      <ol className="flex flex-col gap-3">
        {events.map((event) => (
          <li
            key={event.id}
            data-testid={`event-timeline-item-${String(event.id)}`}
            className="relative rounded-[10px] border border-[#eee] bg-white p-4"
          >
            <div className="mb-1 text-[11px] uppercase tracking-[1px] text-[#999]">
              {formatTimestamp(event.timestamp)}
            </div>
            <div className="mb-2 text-[14px] font-semibold text-[#333]">{event.eventTypeLabel}</div>
            <details className="text-[12px] text-[#666]">
              <summary className="cursor-pointer select-none text-[#6B0F1A] hover:underline">
                View payload
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-[#f8f9fa] p-3 font-mono text-[11px] text-[#333]">
                {JSON.stringify(event.message, null, 2)}
              </pre>
            </details>
          </li>
        ))}
      </ol>

      <div className="flex items-center justify-between border-t border-[#eee] pt-3 text-[12px] text-[#666]">
        <span data-testid="event-timeline-count">
          Showing {String(events.length)} of {String(total)}
        </span>
        {hasMore && (
          <Button
            size="sm"
            variant="outline"
            data-testid="event-timeline-load-more"
            disabled={isLoading}
            onClick={() => setOffset((current) => current + PAGE_SIZE)}
          >
            {isLoading ? 'Loading...' : 'Load more'}
          </Button>
        )}
      </div>
    </div>
  );
}
